import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import EmailIcon from "../../../../assets/images/admin/email.svg";
import { X, Upload } from "lucide-react";
import { PlatformPills } from "../components/PlatformPills";
import AddFieldModal from "../components/AddFieldModal";
import URLActionBar from "../components/URLActionBar";
import { SketchPicker } from "react-color";
import FanActions from "../components/FanActions";
import SocialActions from "./components/SocialActions";
import BioLinkPreview from "../LinkPreview/BioLinkPreview";
import { showToast } from "../../../../helper/ShowToast";
import { buildLinkMap } from "../LinkHelper";
import { suggestBioShortIdApi } from "../../../../api/artistManager/smartLink.api";
import { SocialPlatformModal } from "../PlatformModals/SocialPlatformModal";
import { StreamingPlatformModal } from "../PlatformModals/StreamingPlatformModal";
import { ToastContainer } from "react-toastify";
import { BioLinkSkeleton } from "../LinkSkeletons";
import { RightPreviewSkeleton } from "../LinkSkeletons";
import LinkAnalytics from "../components/LinkAnalytics";
import ReorderPlatforms from "../components/ReorderPlatforms";
import { socialPlatformsArray } from "../constants/socialPlatforms";
import { bioFanActions as fanActionsDefault } from "../constants/fanActions";
import { BIO_STREAMING_PLATFORMS as streamingPlatformsArray } from "../constants/streamingPlatforms";
import { socialActionsDefault } from "../constants/socialActionPlatform";
import {
    useCreateBioAndTourLink,
    useSmartLinkDetails,
    useUpdateBioAndTourLink
} from "../../../../queries/artistManger/smartLink.query";
import AppTour from "../../../../components/AppTour/AppTour";
import { bioLinkTourSteps } from "../../../../components/AppTour/ModuleTourSteps";

const BIO_LINK_DOMAIN = "https://upnc.me";

const slugifyShortId = (text) =>
    String(text || "")
        .toLowerCase()
        .replace(/[\s_]+/g, "-")
        .replace(/[^\w-]+/g, "")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");

export default function BioLink({
    embedded = false,
    linkId: linkIdProp,
    onBack,
    onSuccess,
    hideAnalytics = false,
} = {}) {
    const navigate = useNavigate();
    const { id: paramId } = useParams();
    const id = linkIdProp || paramId;
    const location = useLocation();
    const isEditMode = Boolean(id);
    const [shortIdEdited, setShortIdEdited] = useState(false);
    const artistDetails = useSelector(state => state.artistDetails);
    const { artistInfo } = artistDetails;
    const fileInputRef = useRef(null);
    const [runTour, setRunTour] = useState(false)
    const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
    const [isStreamingModalOpen, setIsStreamingModalOpen] = useState(false);
    const [openForm, setOpenForm] = useState(false)
    const [expandedPill, setExpandedPill] = useState(null);
    const [expandedStreamingPill, setExpandedStreamingPill] = useState(null);
    const [activeTab, setActiveTab] = useState("Build");
    const tabs = hideAnalytics ? ["Build"] : ["Build", "Analytics"];
    const [bioLinkData, setBioLinkData] = useState({
        bioTitle: '',
        bioDescription: '',
        image: '',
        artistName: '',
        imageSource: "artist",   // "artist" | "uploaded"
        imageUrl: "",            // final image URL used
        imageFile: null,         // File if uploaded
        imageFileName: "",
        imageChanged: false,
        pageLayout: 'modern_layout',
        selectedBackground: 'color',
        selectedColor: "#5CBDC7",
        background: 'color',
        bgImagePreview: "",
        bgImageFile: null,
        bgImageFileName: "",
        bgImageChanged: false,
        shortId: '',
        // step 2
        emailCollection: {
            enabled: true,
            actionName: "Subscribe"
        },
        fanActions: fanActionsDefault.map(action => ({
            ...action,
            enabled: false,
            url: ""
        }))
    });
    const [socialActions, setSocialActions] = useState(
        socialActionsDefault.map(item => ({
            ...item,
            enabled: false,
            url: ""
        }))
    );
    const [socialPlatforms, setSocialPlatforms] = useState(socialPlatformsArray);
    const [streamingPlatforms, setStreamingPlatforms] = useState(streamingPlatformsArray);
    const [orderedList, setOrderedList] = useState([]);
    const goBack = () => {
        if (onBack) onBack();
        else navigate(-1);
    };

    const {
        mutate: createBioLink,
        isPending: isCreating,
    } = useCreateBioAndTourLink({
        navigate: embedded ? undefined : navigate,
        onSuccess: embedded
            ? (res) => onSuccess?.(res?.data || res)
            : undefined,
    })

    const {
        data: bioLinkDetails,
        isLoading: linkDetailsLoading
    } = useSmartLinkDetails(id, isEditMode);

    const {
        mutate: updateBioLink,
        isPending: isUpdating,
    } = useUpdateBioAndTourLink({
        onSuccess: embedded
            ? (res) => onSuccess?.(res?.data || res)
            : undefined,
    })

    const isLoading = isCreating || isUpdating;

    useEffect(() => {
        if (embedded) return;
        if (location.hash === "#start-tour") {
            setRunTour(true)
        }
    }, [location.hash, embedded])


    // useEffect(() => {
    //     if (
    //         !isEditMode &&
    //         artistInfo?.avatar &&
    //         bioLinkData.imageSource === "artist"
    //     ) {
    //         setBioLinkData(prev => ({
    //             ...prev,
    //             imageUrl: artistInfo.avatar,
    //             artistName: artistInfo.name,
    //             bioTitle: artistInfo.name
    //         }));
    //     }
    //     setBioLinkData(prev => ({
    //         ...prev,
    //         artistName: artistInfo.name,
    //     }));
    //     if (artistInfo?.links) {
    //         const linkMap = buildLinkMap(artistInfo.links);
    //         // Update social
    //         setSocialPlatforms(prev =>
    //             prev.map(platform => {
    //                 const match = linkMap[platform.name.toLowerCase()];
    //                 return match
    //                     ? {
    //                         ...platform,
    //                         url: match,
    //                         actionName: platform.name
    //                     }
    //                     : platform;
    //             })
    //         );
    //         //STREAMING (ONLY MATCH FROM STREAMING ARRAY)
    //         setStreamingPlatforms(prev =>
    //             prev.map(platform => {
    //                 const platformKey = platform.name.toLowerCase();

    //                 if (linkMap[platformKey]) {
    //                     return {
    //                         ...platform,
    //                         url: linkMap[platformKey],
    //                         enabled: true,
    //                         actionName: platform.actionName
    //                     };
    //                 }

    //                 return platform;
    //             })
    //         );
    //         setSocialActions(prev =>
    //             prev.map(action => {
    //                 const match = linkMap[action.id];
    //                 return {
    //                     ...action,
    //                     url: match || "",
    //                     enabled: !!match
    //                 };
    //             })
    //         );
    //     }
    // }, [artistInfo, bioLinkData.imageSource]);

    useEffect(() => {
        // DO NOT run in edit mode
        if (isEditMode) return;

        if (
            artistInfo?.avatar &&
            bioLinkData.imageSource === "artist"
        ) {
            setBioLinkData(prev => ({
                ...prev,
                imageUrl: artistInfo.avatar,
                artistName: artistInfo.name,
                bioTitle: artistInfo.name
            }));
        }

        setBioLinkData(prev => ({
            ...prev,
            artistName: artistInfo?.name || "",
        }));

        if (artistInfo?.links) {
            const linkMap = buildLinkMap(artistInfo.links);

            // SOCIAL FOOTER
            setSocialPlatforms(prev =>
                prev.map(platform => {
                    const match = linkMap[platform.name.toLowerCase()];
                    return match
                        ? { ...platform, url: match }
                        : platform;
                })
            );

            // STREAMING
            setStreamingPlatforms(prev =>
                prev.map(platform => {
                    const key = platform.name.toLowerCase();

                    if (linkMap[key]) {
                        return {
                            ...platform,
                            url: linkMap[key],
                            enabled: true
                        };
                    }
                    return platform;
                })
            );

            // SOCIAL ACTIONS
            setSocialActions(prev =>
                prev.map(action => {
                    const match = linkMap[action.id];
                    return {
                        ...action,
                        url: match || "",
                        enabled: !!match
                    };
                })
            );
        }

    }, [artistInfo]);

    useEffect(() => {
        if (isEditMode || shortIdEdited) return;
        const title = bioLinkData.bioTitle?.trim();
        if (!title) {
            setBioLinkData((prev) => (prev.shortId ? { ...prev, shortId: "" } : prev));
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const result = await suggestBioShortIdApi(title);
                if (cancelled) return;
                const nextShortId = result?.data?.shortId || slugifyShortId(title);
                setBioLinkData((prev) => (
                    prev.shortId === nextShortId ? prev : { ...prev, shortId: nextShortId }
                ));
            } catch {
                if (cancelled) return;
                const nextShortId = slugifyShortId(title);
                setBioLinkData((prev) => (
                    prev.shortId === nextShortId ? prev : { ...prev, shortId: nextShortId }
                ));
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [bioLinkData.bioTitle, isEditMode, shortIdEdited]);

    useEffect(() => {
        if (!isEditMode || !bioLinkDetails) return;
        const data = bioLinkDetails;
        // ---------- BASIC ----------
        setBioLinkData(prev => ({
            ...prev,
            bioTitle: data.title || "",
            bioDescription: data.description || "",
            imageUrl: data.image || "",
            imageSource: data.imageSource || "biolink",
            imageFile: null,
            imageFileName: "",
            imageChanged: false,
            bgImagePreview: data.customization?.backgroundImageUrl || "",
            bgImageFile: null,
            bgImageFileName: "",
            bgImageChanged: false,
            shortId: data.shortId || "",

            selectedBackground: data.customization?.backgroundType,
            selectedColor: data.customization?.backgroundColor,

            emailCollection: data.customization?.emailCollection || prev.emailCollection
        }));

        // ---------- STORES ----------
        const stores = data.customization?.stores || [];
        const enabledStores = data.customization?.enabledStores || [];

        const storeMap = {};
        stores.forEach(s => {
            storeMap[s.storeId] = s.url;
        });
        const enabledMap = {};
        enabledStores.forEach(s => {
            enabledMap[s.storeId] = s.actionName;
        });

        // ---------- STREAMING ----------
        setStreamingPlatforms(prev =>
            prev.map(p => {
                const key = String(p.id).toLowerCase();
                if (storeMap[key]) {
                    return {
                        ...p,
                        url: storeMap[key],
                        enabled: true,
                        actionName: enabledMap[key] || p.actionName
                    };
                }
                return p;
            })
        );

        // ---------- FAN ACTIONS ----------

        setBioLinkData(prev => ({
            ...prev,
            fanActions: prev.fanActions.map(action => {
                const key = action.id;

                if (storeMap[key]) {
                    let updated = {
                        ...action,
                        enabled: true,
                        url: storeMap[key],
                        actionName: enabledMap[key] || action.actionName
                    };

                    // HANDLE WHATSAPP SPLIT
                    if (key === "whatsapp_to_artist" || key === "textmessage_to_artist") {
                        const match = storeMap[key].match(/(\d+)/);

                        if (match) {
                            const fullNumber = match[1];

                            updated.phone = fullNumber.slice(-10);

                            updated.country = {
                                value: "IN",
                                label: "India",
                                dialCode: fullNumber.slice(0, fullNumber.length - 10)
                            };
                        }
                    }

                    return updated;
                }

                return action;
            })
        }));

        // ---------- SOCIAL ACTIONS ----------
        const socialLinks =
            bioLinkDetails?.customization?.socialIcons?.socialLinks || {};

        // SOCIAL FOOTER
        setSocialPlatforms(prev =>
            prev.map(platform => {
                const key = platform.name.toLowerCase();
                if (socialLinks[key]) {
                    return {
                        ...platform,
                        url: socialLinks[key],
                        enabled: true,
                        actionName: enabledMap[key] || platform.actionName
                    };
                }

                return {
                    ...platform,
                    url: "",
                    enabled: false
                };
            })
        );

        // IMPORTANT: ALSO UPDATE SOCIAL ACTIONS
        setSocialActions(prev =>
            prev.map(action => {
                const key = action.id; // already lowercase
                if (socialLinks[key]) {
                    return {
                        ...action,
                        url: socialLinks[key],
                        enabled: true,
                        actionName: enabledMap[key] || action.actionName
                    };
                }

                return {
                    ...action,
                    url: "",
                    enabled: false
                };
            })
        );

    }, [bioLinkDetails, isEditMode]);

    useEffect(() => {
        // EDIT MODE → use backend order
        if (isEditMode && bioLinkDetails) {
            const storeOrder =
                bioLinkDetails?.customization?.stores?.map(s => s.storeId) || [];

            const all = getAllSelectedPlatforms();
            const map = new Map(all.map(p => [p.id, p]));

            const ordered = storeOrder
                .map(id => map.get(id))
                .filter(Boolean);

            const remaining = all.filter(p => !storeOrder.includes(p.id));

            setOrderedList([...ordered, ...remaining]);
            return;
        }
        // CREATE MODE → build directly from selected platforms
        const all = getAllSelectedPlatforms();
        setOrderedList(all);

    }, [isEditMode, bioLinkDetails, streamingPlatforms, bioLinkData.fanActions, socialActions]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("tab") === "analytics") {
            setActiveTab("Analytics");
        }
    }, [location.search]);

    useEffect(() => {
        return () => {
            if (bioLinkDetails?.bgImagePreview) {
                URL.revokeObjectURL(bioLinkDetails.bgImagePreview);
            }
        };
    }, [bioLinkDetails?.bgImagePreview]);



    const openBgFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handlePillClick = (id) => {
        setExpandedPill(expandedPill === id ? null : id);
    };

    const handleStreamingPillClick = (id) => {
        setExpandedStreamingPill(expandedStreamingPill === id ? null : id);
    };

    const socialPlatformsUrlChange = (id, newUrl) => {
        setSocialPlatforms(
            socialPlatforms.map((p) => (p.id === id ? { ...p, url: newUrl } : p))
        );
    };

    const streamingPlatformsUrlChange = (id, newUrl) => {
        setStreamingPlatforms(
            streamingPlatforms.map((p) => (p.id === id ? { ...p, url: newUrl } : p))
        );
    };
    const handleCollapse = (e, id) => {
        e.stopPropagation();
        setExpandedPill(null);
    };

    const handleStreamingCollapse = (e, id) => {
        e.stopPropagation();
        setExpandedStreamingPill(null);
    };

    const userHasAnyUrl = socialPlatforms.some(p => p.url && p.url.trim() !== "");
    const activePlatforms = userHasAnyUrl
        ? socialPlatforms
        : socialPlatformsArray;

    const handleInputChange = (field, value) => {
        setBioLinkData(prev => ({ ...prev, [field]: value }));
    };


    const handleBgImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            showToast('warning', "Please select a valid image file")
            return;
        }

        const previewURL = URL.createObjectURL(file);

        setBioLinkData(prev => ({
            ...prev,
            bgImageFile: file,
            bgImageFileName: file.name,
            bgImagePreview: previewURL,
            bgImageChanged: true,
        }));
    };

    const removeBgImage = () => {
        if (bioLinkData?.bgImagePreview) URL.revokeObjectURL(bioLinkData.bgImagePreview);
        setBioLinkData({ ...bioLinkData, bgImageFile: null, bgImageFileName: "", bgImagePreview: "" })
    };

    const socialPlatformsWithUrl = socialPlatforms.filter(
        (p) => p.url && p.url.trim() !== ""
    );

    const streamingPlatformsWithUrl = streamingPlatforms.filter(
        (p) => p.url && p.url.trim() !== ""
    );

    const handleFanActionToggle = (index, value) => {
        setBioLinkData(prev => {
            const updated = [...prev.fanActions];
            updated[index].enabled = value;
            return { ...prev, fanActions: updated };
        });
    };

    const handleFanActionChange = (index, field, value) => {
        setBioLinkData(prev => {
            const updated = [...prev.fanActions];
            updated[index][field] = value;
            return { ...prev, fanActions: updated };
        });
    };

    const handleSocialToggle = (index, value) => {
        setSocialActions(prev => {
            const updated = [...prev];
            updated[index].enabled = value;
            return updated;
        });
    };

    const handleSocialChange = (index, field, value) => {
        setSocialActions(prev => {
            const updated = [...prev];
            updated[index][field] = value;

            if (field === "url" && value) {
                updated[index].enabled = true;
            }

            return updated;
        });
    };

    // const validFanActions = (bioLinkData?.fanActions || []).filter(
    //     (action) =>
    //         action.enabled &&
    //         action.actionName?.trim() &&
    //         action.url?.trim()
    // );
    const validFanActions = (bioLinkData?.fanActions || []).filter(
        (action) => {
            if (!action.enabled || !action.actionName?.trim()) return false;

            if (
                action.id === "whatsapp_to_artist" ||
                action.id === "textmessage_to_artist"
            ) {
                return action.phone && action.country;
            }
            return action.url?.trim();
        }
    );

    const validSocialActions = socialActions.filter(
        a => a.enabled && a.url && a.actionName?.trim()
    );

    const updateEmailCollection = (data) => {
        setBioLinkData(prev => ({
            ...prev,
            emailCollection: {
                ...prev.emailCollection,
                ...data
            }
        }));
    };

    const getAllSelectedPlatforms = () => {
        const emailItem = bioLinkData.emailCollection?.enabled
            ? [{
                id: "email_subscribe",
                name: "Email Subscribe",
                icon: EmailIcon,
                type: "email",
                ...bioLinkData.emailCollection
            }]
            : [];

        return [
            ...streamingPlatforms.filter(p => p.enabled && p.url),
            ...bioLinkData.fanActions.filter(p => p.enabled),
            ...socialActions.filter(p => p.enabled),
            ...emailItem
        ];
    };

    const orderedPreview = orderedList.map(p => {
        if (p.id === "whatsapp_to_artist") {
            return {
                ...p,
                url: `https://wa.me/${p.country?.dialCode}${p.phone}`
            };
        }

        if (p.id === "textmessage_to_artist") {

            return {
                ...p,
                url: `sms://${p.country?.dialCode}${p.phone}`
            };
        }

        return p;
    });
    const previewData = {
        linkType: 'biolink',
        background: {
            type: bioLinkData?.selectedBackground,
            color: bioLinkData?.selectedColor,
            image: bioLinkData?.bgImagePreview,
            fallback: "#5CBDC7"
        },
        title: bioLinkData?.bioTitle,
        description: bioLinkData?.bioDescription,
        image: bioLinkData?.imageUrl || bioLinkData?.imageFile ||
            require("../../../../assets/images/karan.png"),

        socialPlatforms: activePlatforms
            .filter(p => (userHasAnyUrl ? p.url : true))
            .map(p => ({
                id: p.id,
                name: p.name,
                url: p.url || "#",
                icon: p.icon
            })),

        streamingPlatforms: streamingPlatformsWithUrl,
        fanActions: validFanActions.map(action => ({
            id: action.id,
            name: action.name,
            actionName: action.actionName,
            url: action.url,
            icon: action.icon,
            previewBgColor: "bg-white/20",
            bgColor: "bg-white/10",
            width: "w-[34px]"
        })),

        socialAction: validSocialActions.map(action => ({
            id: action.id,
            name: action.name,
            actionName: action.actionName,
            url: action.url,
            icon: action.icon,
            previewBgColor: "bg-white/20",
            bgColor: "bg-white/10",
            width: "w-[34px]"
        })),
        platforms: orderedPreview
    };

    // ---------- STORES ----------
    const buildStores = () => {
        return orderedList.map(p => {
            if (p.phone && p.country) {
                const cleanPhone = p.phone.replace(/\D/g, "");
                const fullNumber = `+${p.country.dialCode}${cleanPhone}`;

                if (p.id === "whatsapp_to_artist") {
                    return {
                        storeId: p.id,
                        url: `https://wa.me/${fullNumber}`
                    };
                }

                if (p.id === "textmessage_to_artist") {
                    return {
                        storeId: p.id,
                        url: `sms://${fullNumber}`
                    };
                }
            }

            return {
                storeId: p.id,
                url: p.url
            };
        });
    };

    // ---------- ENABLED STORES (CTA) ----------
    const buildEnabledStores = () => {
        return orderedList
            .filter(p => p.enabled)
            .map(p => ({
                storeId: String(p.id).toLowerCase(),
                actionName: p.actionName
            }));
    };

    const handlePublish = async () => {
        if (!bioLinkData?.bioTitle) {
            showToast('warning', 'Bio Title is required');
            return;
        }

        if (!bioLinkData?.bioDescription) {
            showToast('warning', 'Bio Description is required');
            return;
        }

        if (bioLinkData.selectedBackground === "image" && !bioLinkData.bgImageFile) {
            showToast("warning", "Please upload a background image.");
            return;
        }

        if (bioLinkData.selectedBackground === "color" && !bioLinkData.selectedColor) {
            showToast("warning", "Please select a background color.");
            return;
        }

        const formData = new FormData();

        // ---------- BASIC ----------
        formData.append("linkType", 'biolink');
        formData.append("title", bioLinkData.bioTitle);
        formData.append("description", bioLinkData.bioDescription);
        formData.append("imageSource", bioLinkData.imageSource);
        formData.append("imageUrl", bioLinkData.imageUrl || "");
        if (bioLinkData.shortId?.trim()) {
            formData.append("shortId", bioLinkData.shortId.trim().toLowerCase());
        }

        // ---------- IMAGES ----------
        if (
            bioLinkData.imageSource === "uploaded" &&
            bioLinkData.imageFile
        ) {
            formData.append("image", bioLinkData.imageFile);
        }

        if (
            bioLinkData.selectedBackground === "image" &&
            bioLinkData.bgImageChanged &&
            bioLinkData.bgImageFile
        ) {
            formData.append("backgroundImage", bioLinkData.bgImageFile);
        }

        // ---------- CUSTOMIZATION ----------
        formData.append(
            "customization",
            JSON.stringify({
                backgroundType: bioLinkData.selectedBackground,
                backgroundColor: bioLinkData.selectedColor,
                stores: buildStores(),
                enabledStores: buildEnabledStores(),
                socialIcons: {
                    enabled: true,
                    socialLinks: socialPlatforms.reduce((acc, p) => {
                        if (p.url) acc[p.name.toLowerCase()] = p.url;
                        return acc;
                    }, {})
                }
            })
        );

        // ---------- EMAIL COLLECTION ----------
        formData.append(
            "emailCollection",
            JSON.stringify(bioLinkData.emailCollection)
        );
        createBioLink(formData)
    };

    const handleUpdate = async () => {
        if (!bioLinkData?.bioTitle) {
            showToast('warning', 'Bio Title is required');
            return;
        }

        if (!bioLinkData?.bioDescription) {
            showToast('warning', 'Bio Description is required');
            return;
        }

        if (bioLinkData.selectedBackground === "image" && !bioLinkData.bgImageFile && bioLinkData.bgImageChanged) {
            showToast("warning", "Please upload a background image.");
            return;
        }

        if (bioLinkData.selectedBackground === "color" && !bioLinkData.selectedColor) {
            showToast("warning", "Please select a background color.");
            return;
        }

        const formData = new FormData();
        const isShortIdChanged = bioLinkData.shortId !== bioLinkDetails.shortId;
        if (isShortIdChanged) {
            if (!/^[a-z0-9-]+$/.test(bioLinkData.shortId)) {
                showToast("warning", "Invalid short link format");
                return;
            }

            if (bioLinkData.shortId.length < 3) {
                showToast("warning", "Short link too short");
                return;
            }
        }
        // ---------- BASIC ----------
        formData.append("title", bioLinkData.bioTitle);
        formData.append("description", bioLinkData.bioDescription);
        formData.append("imageSource", bioLinkData.imageSource);
        formData.append("imageUrl", bioLinkData.imageUrl || "");
        if (isShortIdChanged && bioLinkData.shortId) {
            formData.append("shortId", bioLinkData.shortId);
        }

        // ---------- IMAGES ----------
        if (
            bioLinkData.imageSource === "uploaded" &&
            bioLinkData.imageFile
        ) {
            formData.append("image", bioLinkData.imageFile);
        }

        // Only send bg image if changed (IMPORTANT optimization)
        if (
            bioLinkData.selectedBackground === "image" &&
            bioLinkData.bgImageChanged &&
            bioLinkData.bgImageFile
        ) {
            formData.append("backgroundImage", bioLinkData.bgImageFile);
        }

        // ---------- CUSTOMIZATION ----------
        formData.append(
            "customization",
            JSON.stringify({
                backgroundType: bioLinkData.selectedBackground,
                backgroundColor: bioLinkData.selectedColor,
                stores: buildStores(),
                enabledStores: buildEnabledStores(),
                pageLayout: bioLinkData.pageLayout || "modern_layout",
                socialIcons: {
                    enabled: true,
                    socialLinks: socialPlatforms.reduce((acc, p) => {
                        if (p.url) acc[p.name.toLowerCase()] = p.url;
                        return acc;
                    }, {})
                }
            })
        );

        // ---------- EMAIL COLLECTION ----------
        formData.append(
            "emailCollection",
            JSON.stringify(bioLinkData.emailCollection)
        );
        // ---------- API CALL ----------
        updateBioLink({ id, formData });
    };


    return (
        <>
            <div className={`BuildBioLink-Page font-jakarta ${embedded ? "bg-white" : "bg-gray-50 min-h-screen"}`}>
                {!embedded && <ToastContainer />}
                {!embedded && (
                    <AppTour
                        tourSteps={bioLinkTourSteps}
                        runTour={runTour}
                        setRunTour={setRunTour}
                    />
                )}
                {/* HEADER */}
                <div className={`sticky top-0 z-30 shadow-md px-4 py-3 ${
                    embedded
                        ? "bg-white text-theme_clr1 border-b-2 border-theme_clr1"
                        : "bg-theme_clr1 text-white"
                }`} data-tour="publish">

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                        {/* LEFT: BACK + TITLE */}
                        <div className="flex items-center gap-2 min-w-0">
                            <button
                                type="button"
                                onClick={goBack}
                                className={embedded ? "text-theme_clr1 hover:text-theme_clr2" : ""}
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <h1 className="text-sm sm:text-lg font-semibold truncate">
                                {activeTab === "Build"
                                    ? `${isEditMode ? "Edit" : "Build a"} Bio Link`
                                    : "Bio Link Analytics"}
                            </h1>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto lg:min-w-0 lg:flex-1 lg:justify-end">
                                {((isEditMode && bioLinkDetails?.domain && bioLinkData?.shortId) || (!isEditMode && (bioLinkData?.shortId || shortIdEdited))) && (
                                    <div className="w-full sm:w-[350px] lg:w-auto lg:min-w-[350px] lg:max-w-[640px] lg:flex-1">
                                        <URLActionBar
                                            fullLink={isEditMode ? bioLinkDetails?.fullLink : `${BIO_LINK_DOMAIN}/${bioLinkData.shortId}`}
                                            linkType={'biolink'}
                                            domain={isEditMode ? bioLinkDetails?.domain : BIO_LINK_DOMAIN}
                                            shortId={bioLinkData?.shortId}
                                            isPreview={!isEditMode}
                                            onChangeShortId={(newShortId) => {
                                                if (!isEditMode) setShortIdEdited(true);
                                                setBioLinkData(prev => ({
                                                    ...prev,
                                                    shortId: newShortId
                                                }));
                                            }}
                                        />
                                    </div>
                                )}

                                {activeTab === "Build" && (
                                <button
                                    type="button"
                                    className={`bg-theme_clr2 px-4 sm:px-6 py-2 text-white text-sm sm:text-base font-semibold rounded-full flex items-center justify-center whitespace-nowrap
                                   ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                                    onClick={isEditMode ? handleUpdate : handlePublish}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {isEditMode ? "Saving..." : "Publishing..."}
                                        </div>
                                    ) : (
                                        isEditMode ? "Save Changes" : "Publish"
                                    )}
                                </button>
                                )}

                            </div>
                    </div>

                </div>


                <div className="container py-6">

                    {/* TABS */}
                    {!hideAnalytics && (
                    <div className="lg:w-[calc(100%-374px)] flex bg-white rounded-xl border border-theme_clr1 overflow-hidden mb-6">

                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 py-3 text-sm font-semibold transition
                                          ${(!isEditMode && tab === 'Analytics') ? 'opacity-50 cursor-not-allowed' : ''}
                                          ${activeTab === tab
                                        ? "bg-theme_clr2 text-white"
                                        : "text-theme_clr1 hover:bg-gray-100"
                                    }`}
                                disabled={!isEditMode && tab === 'Analytics'}
                            >
                                {tab}
                            </button>
                        ))}

                    </div>
                    )}


                    <div className="flex flex-col-reverse lg:flex-row gap-6">

                        {/* LEFT PANEL */}

                        <div className="flex-1 lg:w-[calc(100%-374px)] space-y-6">
                            {isEditMode && linkDetailsLoading ? (
                                <BioLinkSkeleton />
                            ) : (
                                <>

                                    {activeTab === "Build" && (
                                        <>
                                            {/* IMAGE CARD */}
                                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-tour="profile-form">
                                                {/* SECTION HEADING */}

                                                <h3 className="text-sm font-semibold text-theme_clr1 uppercase tracking-wide">
                                                    Profile Image
                                                </h3>

                                                <p className="text-sm text-theme_clr2 font-semibold mb-4">
                                                    Upload artwork to display on your bio link page.
                                                    Recommended size: 1000x1000.
                                                </p>

                                                <div className="flex items-center justify-between border rounded-xl p-4">

                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={
                                                                bioLinkData.imageUrl ||
                                                                artistInfo?.avatar ||
                                                                "/placeholder.png"
                                                            }
                                                            className="w-[70px] h-[70px] rounded-xl object-cover"
                                                        />

                                                        <span className="font-semibold text-theme_clr1">
                                                            Your Image
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-col items-end gap-1">

                                                        {/* CHANGE BUTTON */}
                                                        <label className="text-theme_clr2 font-semibold cursor-pointer">
                                                            Change
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                hidden
                                                                onChange={(e) => {
                                                                    const file = e.target.files[0];
                                                                    if (!file) return;

                                                                    const previewUrl = URL.createObjectURL(file);

                                                                    setBioLinkData(prev => ({
                                                                        ...prev,
                                                                        imageSource: "uploaded",
                                                                        imageUrl: previewUrl,
                                                                        imageFile: file,
                                                                        imageFileName: file.name,
                                                                        imageChanged: true
                                                                    }));
                                                                }}
                                                            />
                                                        </label>

                                                        {/* USE ARTIST IMAGE */}
                                                        {bioLinkData.imageSource === "uploaded" && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setBioLinkData(prev => ({
                                                                        ...prev,
                                                                        imageSource: "artist",
                                                                        imageUrl: artistInfo?.avatar || "",
                                                                        imageFile: null,
                                                                        imageFileName: "",
                                                                        imageChanged: true
                                                                    }))
                                                                }
                                                                className="text-xs text-theme_clr2 underline"
                                                            >
                                                                Use artist image
                                                            </button>
                                                        )}

                                                    </div>

                                                </div>


                                                {/* TITLE */}

                                                <div className="mt-6 space-y-4">

                                                    <div>
                                                        <label className="text-sm font-bold text-theme_clr1">
                                                            Title
                                                        </label>

                                                        <p className="text-xs text-theme_clr2 mb-1">
                                                            Artist name or profile title
                                                        </p>

                                                        <input
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme_clr2 outline-none"
                                                            placeholder="Enter title"
                                                            value={bioLinkData?.bioTitle}
                                                            onChange={(e) => handleInputChange('bioTitle', e.target.value)}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="text-sm font-bold text-theme_clr1">
                                                            Title Description
                                                        </label>

                                                        <p className="text-xs text-theme_clr2 mb-1">
                                                            Short description for your bio
                                                        </p>

                                                        <input
                                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme_clr2 outline-none"
                                                            placeholder="Enter description"
                                                            value={bioLinkData?.bioDescription}
                                                            onChange={(e) => handleInputChange('bioDescription', e.target.value)}
                                                        />
                                                    </div>

                                                </div>


                                                {/* BACKGROUND */}

                                                <div className="mt-6">

                                                    <label className="font-semibold text-theme_clr1 block mb-2">
                                                        Background
                                                    </label>

                                                    <div className="grid grid-cols-3 gap-2">

                                                        {["blur", "color", "image"].map((type) => (
                                                            <div
                                                                key={type}
                                                                onClick={() => handleInputChange('selectedBackground', type)}
                                                                className={`cursor-pointer border rounded-lg py-2 text-center font-semibold capitalize
                                                                ${bioLinkData?.selectedBackground === type
                                                                        ? "bg-theme_clr2 text-white border-theme_clr2"
                                                                        : "border-gray-300 text-theme_clr1"}
                                                            `}
                                                            >
                                                                {type}
                                                            </div>
                                                        ))}

                                                    </div>

                                                    <div className="mt-4 flex justify-center">
                                                        {bioLinkData?.selectedBackground === "image" && (
                                                            <div className="w-full max-w-full bg-white rounded-2xl shadow-xl p-8">

                                                                {/* Hidden input MUST be here */}
                                                                <input
                                                                    type="file"
                                                                    ref={fileInputRef}
                                                                    className="hidden"
                                                                    accept="image/*"
                                                                    onChange={handleBgImageChange}   //This calls handleBgImageChange
                                                                />

                                                                {/* IF NO IMAGE SELECTED */}
                                                                {!bioLinkData?.bgImagePreview && (
                                                                    <div
                                                                        className="flex flex-col items-center justify-center w-full h-64 
                                                                   border-2 border-dashed border-gray-300 rounded-xl cursor-pointer 
                                                                     hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-300"
                                                                        onClick={openBgFilePicker}    // opens file dialog
                                                                    >
                                                                        <Upload className="w-12 h-12 text-gray-400 mb-3" />
                                                                        <p className="mb-2 text-sm text-gray-500">
                                                                            <span className="font-semibold">Click to upload</span> or drag & drop
                                                                        </p>
                                                                        <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                                                                    </div>
                                                                )}

                                                                {/* IF IMAGE SELECTED */}
                                                                {bioLinkData?.bgImagePreview && (
                                                                    <div className="relative">
                                                                        <div className="w-full h-64 rounded-xl overflow-hidden bg-gray-100">
                                                                            <img
                                                                                src={bioLinkData?.bgImagePreview}
                                                                                alt="Background"
                                                                                className="w-full h-full object-contain"
                                                                            />
                                                                        </div>

                                                                        <button
                                                                            onClick={removeBgImage}
                                                                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 
                                                    text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                                                                        >
                                                                            <X className="w-5 h-5" />
                                                                        </button>

                                                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                                                            <p className="text-sm text-gray-600 truncate">
                                                                                <span className="font-semibold">File:</span> {bioLinkData?.bgImageFileName}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {bioLinkData?.selectedBackground === "color" &&
                                                            <div>
                                                                <SketchPicker
                                                                    color={bioLinkData?.selectedColor}
                                                                    onChangeComplete={(e) => handleInputChange('selectedColor', e.hex)}
                                                                />
                                                            </div>
                                                        }

                                                    </div>

                                                </div>

                                            </div>
                                            {/* Fan action */}
                                            <div data-tour="fan-actions">
                                                <FanActions
                                                    fanActions={bioLinkData.fanActions}
                                                    onToggle={handleFanActionToggle}
                                                    onChange={handleFanActionChange}
                                                />
                                            </div>

                                            {/* STREAMING ACTION */}

                                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-tour="streaming-actions">

                                                <div className="flex justify-between items-center mb-5">

                                                    <h3 className="font-bold text-theme_clr1 text-lg">
                                                        Streaming Action
                                                    </h3>

                                                    <div className="flex gap-4 text-theme_clr2 font-semibold text-sm">
                                                        <button onClick={() => setIsStreamingModalOpen(true)}>
                                                            <i className="bi bi-plus-circle mr-1"></i>
                                                            Edit Platform
                                                        </button>

                                                    </div>

                                                </div>


                                                <div className="space-y-3">

                                                    <div className="flex flex-wrap gap-2">
                                                        {streamingPlatformsWithUrl.slice(0, 3).map((platform) => (
                                                            <PlatformPills
                                                                platform={platform}
                                                                expandedPill={expandedStreamingPill}
                                                                handlePillClick={handleStreamingPillClick}
                                                                handleCollapse={handleStreamingCollapse}
                                                                handleUrlChange={streamingPlatformsUrlChange}
                                                            />
                                                        ))}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {streamingPlatformsWithUrl.slice(3, 6).map((platform) => (
                                                            <PlatformPills
                                                                platform={platform}
                                                                expandedPill={expandedStreamingPill}
                                                                handlePillClick={handleStreamingPillClick}
                                                                handleCollapse={handleStreamingCollapse}
                                                                handleUrlChange={streamingPlatformsUrlChange}
                                                            />
                                                        ))}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {streamingPlatformsWithUrl.slice(6, 9).map((platform) => (
                                                            <PlatformPills
                                                                platform={platform}
                                                                expandedPill={expandedStreamingPill}
                                                                handlePillClick={handleStreamingPillClick}
                                                                handleCollapse={handleStreamingCollapse}
                                                                handleUrlChange={streamingPlatformsUrlChange}
                                                            />
                                                        ))}
                                                    </div>

                                                </div>

                                            </div>
                                            {/* social action section */}
                                            {/* <SocialActions /> */}
                                            <div data-tour="social-actions">
                                            <SocialActions
                                                socialActions={socialActions}
                                                onToggle={handleSocialToggle}
                                                onChange={handleSocialChange}
                                            />
                                            </div>
                                            {/* COLLECT FAN DATA */}

                                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-tour="collect-fan-data">

                                                <div className="flex justify-between items-start">

                                                    <div>

                                                        <h4 className="font-bold text-theme_clr1">
                                                            Collect Fan Data
                                                        </h4>

                                                        <p className="text-sm text-theme_clr2 mt-1">
                                                            Fans will be asked to subscribe and share email
                                                            and location information.
                                                        </p>

                                                    </div>

                                                    <ToggleSwitch
                                                        size="md"
                                                        onColor="bg-theme_clr2"
                                                        enabled={bioLinkData.emailCollection.enabled}
                                                        setEnabled={(val) =>
                                                            updateEmailCollection({
                                                                enabled: val,
                                                                actionName: val ? bioLinkData.emailCollection.actionName || "Subscribe" : ""
                                                            })
                                                        }
                                                    />

                                                </div>


                                                <div className="mt-4">
                                                    <label className="text-sm font-semibold text-theme_clr1">
                                                        Email Button Text
                                                    </label>

                                                    <input
                                                        className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-theme_clr2 outline-none"
                                                        placeholder="Subscribe"
                                                        value={bioLinkData.emailCollection.actionName}
                                                        disabled={!bioLinkData.emailCollection.enabled}
                                                        onChange={(e) =>
                                                            updateEmailCollection({ actionName: e.target.value })
                                                        }
                                                    />
                                                </div>

                                            </div>


                                            {/* SOCIAL FOOTER */}

                                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-tour="social-footer">

                                                <div className="flex justify-between items-center mb-5">

                                                    <h3 className="font-bold text-theme_clr1 text-lg">
                                                        Social Footer
                                                    </h3>

                                                    <div className="flex gap-4 text-theme_clr2 font-semibold">

                                                        <button onClick={() => setIsSocialModalOpen(true)}>
                                                            <i className="bi bi-plus-circle mr-1"></i>
                                                            Edit Platform
                                                        </button>
                                                    </div>

                                                </div>

                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {socialPlatformsWithUrl.slice(0, 3).map((platform) => (
                                                        <PlatformPills
                                                            platform={platform}
                                                            expandedPill={expandedPill}
                                                            handlePillClick={handlePillClick}
                                                            handleCollapse={handleCollapse}
                                                            handleUrlChange={socialPlatformsUrlChange}
                                                        />
                                                    ))}
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {socialPlatformsWithUrl.slice(3).map((platform) => (
                                                        <PlatformPills
                                                            platform={platform}
                                                            expandedPill={expandedPill}
                                                            handlePillClick={handlePillClick}
                                                            handleCollapse={handleCollapse}
                                                            handleUrlChange={socialPlatformsUrlChange}
                                                        />
                                                    ))}
                                                </div>

                                            </div>



                                        </>
                                    )}
                                    {activeTab === "Analytics" && (
                                        <div className="bg-white border rounded-xl p-6 shadow-sm">
                                            <LinkAnalytics
                                                smartLinkId={id}
                                                type='biolink'
                                            />
                                        </div>

                                    )}
                                </>
                            )}
                        </div>

                        {/* RIGHT PHONE PREVIEW */}
                        {isEditMode && linkDetailsLoading ? (
                            <RightPreviewSkeleton />
                        ) : (
                            <div className="right-section w-[350px] mx-auto mt-6 space-y-4">
                                <div data-tour="mobile-demo">
                                    <BioLinkPreview previewData={previewData} />
                                </div>
                                {activeTab === "Build" ?
                                    <div data-tour="reorder">
                                        <ReorderPlatforms
                                            orderedList={orderedList}
                                            setOrderedList={setOrderedList}
                                        />
                                    </div>
                                    : ''
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* Bottom actions — Growth Engine embedded flow only */}
                {embedded && activeTab === "Build" && (
                    <div className="sticky bottom-0 z-40 bg-white border-t-2 border-theme_clr1 px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={goBack}
                                className="bg-transparent text-theme_clr1 text-sm font-semibold border-2 border-theme_clr1 rounded-full px-4 sm:px-6 py-2 hover:bg-theme_clr1 hover:text-white transition"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                data-tour="publish-bottom"
                                className={`bg-theme_clr2 px-4 sm:px-6 py-2 text-white text-sm sm:text-base font-semibold rounded-full flex items-center justify-center whitespace-nowrap
                                   ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                                onClick={isEditMode ? handleUpdate : handlePublish}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        {isEditMode ? "Saving..." : "Publishing..."}
                                    </div>
                                ) : (
                                    isEditMode ? "Save Changes" : "Publish"
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODALS */}

            <SocialPlatformModal
                isOpen={isSocialModalOpen}
                onClose={() => setIsSocialModalOpen(false)}
                platforms={socialPlatforms}
                onSave={(updated) => setSocialPlatforms(updated)}
            />
            <StreamingPlatformModal
                isOpen={isStreamingModalOpen}
                onClose={() => setIsStreamingModalOpen(false)}
                allPlatforms={streamingPlatforms}
                onSave={(updated) => setStreamingPlatforms(updated)}
            />

            <AddFieldModal
                isOpen={openForm}
                onClose={() => setOpenForm(false)}
            />

        </>
    )
}